import { NIGHTMARES, SIDE } from '@somnhold/content';
import { idiv, imax, mulBp, withinRange } from '../math/fixed.ts';
import type { BattleState, Enemy, Spawn } from '../types.ts';
import { ev } from './combat.ts';
import { KITS } from './unsleeping/kits.ts';

/**
 * The Prestacaras (Caras prestadas 4, docs/SAGAS.md). Its rule: it copies someone who isn't there — one of the group's
 * heroes who isn't in the fight, fighting with the kit of the Unsleeping of that hero's class at a fraction of its
 * strength. Every quarter of its life lost, it takes another absent face. Led under a lantern, its face slips for a
 * few seconds: no kit, and blows land harder. With nobody absent it keeps Cal's face and fights with its claws.
 */

/** Its mask, set as it comes out (spawnDue). */
export const facetakerSpawn = (s: BattleState, e: Enemy, sp: Spawn): void => {
  const c = sp.copy;
  if (!c) return;
  e.mask = { faces: c.faces, at: 0, bare: 0, cd: 0, hold: c.hold, rush: c.rush, speed0: e.speed };
  if (c.rush > 0) e.speed = mulBp(e.speed, SIDE.facetaker.rushBp);
  // Whose face it comes out with ('' for Cal's).
  ev(s, { t: 'face', id: e.id, pid: c.faces[0]?.pid ?? '', x: e.x, y: e.y });
};

/** A blow on it while bare lands harder. */
export const facetakerTakes = (e: Enemy, dmg: number): number => (e.mask && e.mask.bare > 0 ? mulBp(dmg, SIDE.facetaker.bareTakenBp) : dmg);

/**
 * Its turn before anything else: 'hold' (standing still as Mada says goodbye: nothing else this tick), 'bare' (no
 * kit: it only walks and claws) or null (its borrowed kit acts as usual).
 */
export const facetakerTick = (s: BattleState, e: Enemy): 'hold' | 'bare' | null => {
  const m = e.mask;
  if (!m) return null;
  if (m.hold > 0) {
    m.hold--;
    e.anim = 'idle';
    return 'hold';
  }
  if (m.rush > 0 && --m.rush === 0) e.speed = m.speed0;
  if (m.cd > 0) m.cd--;
  const k = SIDE.facetaker;
  // Another face every quarter of its life.
  const hpBp = idiv(e.hp * 10000, imax(1, e.maxHp));
  const next = k.swapAtBp[m.at];
  if (next !== undefined && hpBp <= next && m.faces.length > 1) {
    m.at++;
    const f = m.faces[m.at % m.faces.length] as (typeof m.faces)[number];
    const id = NIGHTMARES[f.kind].unsleeping;
    if (e.un && id) {
      e.kind = f.kind;
      e.un.id = id;
      e.un.cds = [];
      e.un.k = [];
      e.un.busy = 0;
      e.un.cast = 0;
      e.un.spots = [];
      e.un.tgts = [];
      e.un.orbs = [];
      KITS[id].init(s, e, e.un);
    }
    ev(s, { t: 'face', id: e.id, pid: f.pid, x: e.x, y: e.y });
  } else if (next !== undefined && hpBp <= next) m.at++;
  // Near a lantern's light the face slips: its own feet in the light, or the hero it fights standing in it (a borrowed
  // bow keeps its distance, so the light on whoever it aims at counts too).
  const lit = s.rules?.lanterns;
  const inLight = (x: number, y: number): boolean => !!lit?.at.some((l) => withinRange(x, y, l.x, l.y, lit.r));
  const facing = s.heroes.some((h) => h.downTicks === 0 && withinRange(h.x, h.y, e.x, e.y, k.fightRange) && inLight(h.x, h.y));
  if (m.bare === 0 && m.cd === 0 && (inLight(e.x, e.y) || facing)) {
    m.bare = k.bareTicks;
    m.cd = k.bareTicks + k.bareCd;
    if (e.un) {
      e.un.cast = 0;
      e.un.busy = 0;
      e.un.spots = [];
    }
    ev(s, { t: 'unmask', id: e.id, face: 'facetaker', x: e.x, y: e.y });
  }
  if (m.bare > 0) {
    m.bare--;
    return 'bare';
  }
  return null;
};
